import * as React from "react";
import RetractableInput from "../../app/Common/Forms/RetractableInput";
import { getTextWidth } from "../../app/Utilities/Generic/Strings";

type Props = {
    store: string;
    username: string;
    password: string;
    onStoreChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onLogin: () => void;
    showStore?: boolean;
    disabled?: boolean;
};

type State = {
    urlWidth: number;
};

export default class LoginForm extends React.Component<Props, State> {
    public storeRef = React.createRef<HTMLInputElement>();
    public usernameRef = React.createRef<HTMLInputElement>();

    constructor(props: Readonly<Props>) {
        super(props);

        this.state = {
            urlWidth: getTextWidth(props.store, "1rem 'Roboto'"),
        };
    }

    public componentDidMount(): void {
        if(this.storeRef.current) {
            this.storeRef.current.focus();
        } else if(this.usernameRef.current) {
            this.usernameRef.current.focus();
        }

        if(!document.fonts.check("1rem 'Roboto'")) {
            document.fonts.ready.then(() => {
                this.setState({
                    urlWidth: getTextWidth(this.props.store, "1rem 'Roboto'"),
                });
            });
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.store !== this.props.store) {
            this.setState({
                urlWidth: getTextWidth(this.props.store, "1rem 'Roboto'"),
            });
        }
    }

    public handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if(e.key === "Enter") {
            this.props.onLogin();
        }
    };

    public handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
    };

    public render(): React.ReactNode {
        return (
            <div id="login-password">
                <form
                    onSubmit={this.handleSubmit}
                    autoComplete="shopfront-off" /* To prevent Chrome from complaining */
                >
                    {this.props.showStore && (
                        <RetractableInput
                            label="Store URL"
                            hasValue={!!this.props.store}
                            style={{ marginBottom: "1rem" }}
                        >
                            {(onFocus, onBlur, focus): React.ReactNode => (
                                <React.Fragment>
                                    <input
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                        type="text"
                                        autoComplete="off"
                                        value={this.props.store}
                                        onChange={this.props.onStoreChange}
                                        ref={this.storeRef}
                                        data-testid="gl-url"
                                        disabled={this.props.disabled}
                                        className="url-input"
                                    />
                                    <span
                                        className="url-suffix"
                                        style={{
                                            fontSize: "0.8em",
                                            left    : `min(100% - 121px, 1.5em + ${this.state.urlWidth}px)`,
                                            opacity : (focus ? "1" : "0"),
                                        }}
                                    >
                                        .onshopfront.com
                                    </span>
                                </React.Fragment>
                            )}
                        </RetractableInput>
                    )}
                    <RetractableInput
                        label="Username"
                        hasValue={!!this.props.username}
                    >
                        {(onFocus, onBlur): React.ReactNode => (
                            <input
                                onFocus={onFocus}
                                onBlur={onBlur}
                                type="text"
                                autoComplete="shopfront-field-u"
                                name="shopfront-field-u"
                                id="shopfront-field-u"
                                value={this.props.username}
                                onChange={this.props.onUsernameChange}
                                ref={this.usernameRef}
                                data-testid="gl-username"
                                disabled={this.props.disabled}
                            />
                        )}
                    </RetractableInput>
                    <RetractableInput
                        label="Password"
                        hasValue={!!this.props.password}
                    >
                        {(onFocus, onBlur): React.ReactNode => (
                            <input
                                onFocus={onFocus}
                                onBlur={onBlur}
                                type="password"
                                autoComplete="shopfront-field-p"
                                name="shopfront-field-p"
                                id="shopfront-field-p"
                                value={this.props.password}
                                onChange={this.props.onPasswordChange}
                                onKeyDown={this.handleKeyDown}
                                data-testid="gl-password"
                                disabled={this.props.disabled}
                            />
                        )}
                    </RetractableInput>
                </form>
            </div>
        );
    }
}
